import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { collection, query, orderBy, getDocs, getDoc, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { FaEdit, FaTrash, FaEye, FaPencilAlt, FaSortUp, FaSortDown, FaSort, FaFilter, FaFileAlt } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchInput from '../components/SearchInput';
import OrderView from '../components/OrderView';
import { 
  ORDER_STATUSES,
  LINE_STATUSES,  // We might need this for status checks
  SIZES,
  LINE_TYPES,
  getOrderStatusDisplay
} from '../firebase/schema';
import NewOrder from './NewOrder';
import NewQuote from './NewQuote';
import Modal from '../components/Modal';
import { useTheme } from '@emotion/react';
import EditOrder from '../components/EditOrder';
import SuccessAnimation from '../components/SuccessAnimation';
import { formatDate } from '../utils/dateFormatter';
import {
  getLineTypeColor,
  getLineTypeShortLabel,
  normalizeLineType,
  sortLineTypes,
  LINE_TYPE_DISPLAY_ORDER
} from '../utils/lineTypeColors';
import ReorderModal from '../components/ReorderModal';
import Quote from '../components/Quote';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

const generateUniqueId = (() => {
  let counter = 0;
  return (prefix = '') => {
    counter += 1;
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
  };
})();

const OrdersContainer = styled.div`
  padding: 80px 1% 20px 1%;
  width: 98%;
  margin: 0 auto;
  background: var(--al-bg);
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  color: white;

  @media (max-width: 768px) {
    padding: 70px 14px 20px 14px;
    width: 100%;
    box-sizing: border-box;
  }
`;

const PageHeader = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 0;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    margin-bottom: 14px;
    gap: 8px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    gap: 6px;
    flex-shrink: 0;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const SearchWrapper = styled.div`
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterLabel = styled.span`
  font-size: 0.75em;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 32px 6px 12px;
  font-size: 0.8em;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: rgba(255, 255, 255, 0.12);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  option {
    background: var(--al-bg);
    color: white;
    text-transform: none;
  }
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-family: 'Montserrat', sans-serif;

  /* Hide Project Manager, Due Date, and Lines columns on mobile.
     Column order (1-indexed): Customer | Project Manager | Due Date | Status |
     Lines | Pieces | Type | Actions. */
  @media (max-width: 768px) {
    table-layout: auto;

    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(5),
    td:nth-child(5) {
      display: none;
    }

    th,
    td {
      padding: 8px 4px;
      font-size: 0.8em;
    }

    /* Align first/last cells flush with the page padding so content lines up with the search bar and buttons above. */
    th:first-of-type,
    td:first-of-type {
      padding-left: 0;
    }
    th:last-of-type,
    td:last-of-type {
      padding-right: 0;
    }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
`;

const SortableTh = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.active ? props.theme.colors.primary : 'rgba(255, 255, 255, 0.6)'};
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    margin-left: 6px;
    font-size: 0.9em;
    vertical-align: middle;
  }
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: white;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.02em;
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 2px 4px;
    font-size: 0.85em;

    &.hide-mobile {
      display: none;
    }
  }
`;

const ActionsCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
  text-align: right;

  @media (max-width: 768px) {
    padding: 4px 0;
    width: 1%;
  }
`;

const LineTypeCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  width: 1%;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 8px 4px;
  }
`;

const LineTypeBadgeRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

const LineTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color};
  color: var(--al-bg);
  padding: 1px 4px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.55em;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1;
  min-width: 16px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 1px 3px;
    font-size: 0.5em;
    min-width: 14px;
  }
`;

const LineTypeEmpty = styled.span`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85em;
`;

const CreateOrderButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s;
  letter-spacing: 0.05em;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const StatusText = styled.span`
  color: ${props => {
    switch (props.status) {
      case ORDER_STATUSES.CREATED:
        return '#FFA500'; // Orange for new orders
      case ORDER_STATUSES.READY_TO_ORDER:
        return props.theme.colors.primary;
      case ORDER_STATUSES.ORDERED:
        return '#1E90FF'; // Blue for ordered
      case ORDER_STATUSES.PARTIALLY_RECEIVED:
        return '#87CEEB'; // Light blue for partially received
      case ORDER_STATUSES.RECEIVED:
        return 'var(--al-primary)'; // Pale green for received
      case ORDER_STATUSES.PARTIALLY_READY_TO_PRINT:
        return '#DDA0DD'; // Plum for partially ready to print
      case ORDER_STATUSES.READY_TO_PRINT:
        return 'var(--al-primary)'; // Pale green for ready to print
      case ORDER_STATUSES.READY_TO_INVOICE:
        return '#FFD700'; // Gold for ready to invoice
      default:
        return 'white';
    }
  }};
  font-weight: 600;
`;

const NumberText = styled.span`
  font-family: 'Montserrat', sans-serif;
  color: white;
  font-weight: 500;
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}; // This should match the navbar hover green
    cursor: pointer;
  }
`;

const LinesFraction = styled.span`
  font-family: 'Montserrat', sans-serif;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  
  // Optional: Style for the completed number
  .completed {
    color: ${props => props.theme.colors.primary};
  }
`;

const ReadyToOrderButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfirmDeleteModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ConfirmDeleteContent = styled.div`
  background: var(--al-bg);
  padding: 30px;
  border-radius: 4px;
  width: 400px;
  text-align: center;

  h3 {
    color: white;
    margin-bottom: 20px;
  }

  .buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
  }
`;

const DeleteButton = styled(ActionButton)`
  background: #dc3545;
  color: white;
  
  &:hover {
    background: #c82333;
  }
`;

const CancelButton = styled(ActionButton)`
  background: #363636;
  color: white;
  border: 1px solid ${props => props.theme.colors.border};
  
  &:hover {
    background: #404040;
  }
`;

const CreateButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: black;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }

  @media (max-width: 768px) {
    flex: 0 0 auto;
    padding: 8px 14px;
    font-size: 0.8rem;

    &.hide-mobile {
      display: none;
    }
  }
`;

const ProgressCell = styled.td`
  font-family: 'Montserrat', sans-serif;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  .total {
    color: white;
  }
  
  .printed {
    color: ${props => props.theme.colors.primary};
  }
`;

const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { facilityId } = useFacility();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteToEdit, setQuoteToEdit] = useState(null);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const theme = useTheme();
  const [showSuccess, setShowSuccess] = useState(false);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [generatingPDF, setGeneratingPDF] = useState(null); // Track which order is generating PDF
  const [orderToDownload, setOrderToDownload] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const orderPdfRef = React.useRef(null);
  const [pdfActionOrder, setPdfActionOrder] = useState(null);
  const [customerEmailForPdf, setCustomerEmailForPdf] = useState(null);
  const [pdfActionCustomerLoading, setPdfActionCustomerLoading] = useState(false);
  const [pdfEmailSending, setPdfEmailSending] = useState(false);
  const [lineIdSearchPending, setLineIdSearchPending] = useState(null); // Track pending line ID search

  // Sort and filter orders
  const sortedAndFilteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.customerName?.toLowerCase().includes(query) ||
        order.projectManager?.toLowerCase().includes(query) ||
        String(order.poNumber || '').toLowerCase().includes(query)
      );
    }
    
    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.key) {
          case 'customerName':
            aVal = (a.customerName || '').toLowerCase();
            bVal = (b.customerName || '').toLowerCase();
            break;
          case 'projectManager':
            aVal = (a.projectManager || '').toLowerCase();
            bVal = (b.projectManager || '').toLowerCase();
            break;
          case 'dueDate':
            aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
            bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
            break;
          case 'status':
            // Custom status order
            const statusOrder = {
              [ORDER_STATUSES.CREATED]: 0,
              [ORDER_STATUSES.READY_TO_ORDER]: 1,
              [ORDER_STATUSES.ORDERED]: 2,
              [ORDER_STATUSES.PARTIALLY_RECEIVED]: 3,
              [ORDER_STATUSES.RECEIVED]: 4,
              [ORDER_STATUSES.PARTIALLY_READY_TO_PRINT]: 5,
              [ORDER_STATUSES.READY_TO_PRINT]: 6,
              [ORDER_STATUSES.READY_TO_INVOICE]: 7,
              [ORDER_STATUSES.COMPLETED]: 8
            };
            aVal = statusOrder[a.status] ?? 99;
            bVal = statusOrder[b.status] ?? 99;
            break;
          case 'totalLines':
            aVal = a.totalLines || 0;
            bVal = b.totalLines || 0;
            break;
          case 'totalQuantity':
            aVal = a.totalQuantity || 0;
            bVal = b.totalQuantity || 0;
            break;
          case 'lineType': {
            // Group rows by their first (canonical-order) line type; orders with
            // no detected line type sort last.
            const primaryIndex = (order) => {
              const first = order.lineTypes?.[0];
              if (!first) return 999;
              const idx = LINE_TYPE_DISPLAY_ORDER.indexOf(first);
              return idx === -1 ? 999 : idx;
            };
            aVal = primaryIndex(a);
            bVal = primaryIndex(b);
            break;
          }
          case 'createdAt':
            aVal = a.createdAt?.seconds || 0;
            bVal = b.createdAt?.seconds || 0;
            break;
          default:
            return 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [orders, statusFilter, searchQuery, sortConfig]);

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // New column, default to ascending (except for dates which default to desc)
      return { key, direction: key === 'dueDate' || key === 'createdAt' ? 'desc' : 'asc' };
    });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');
      const querySnapshot = await getDocs(ordersRef);
      
      const ordersData = [];
      querySnapshot.forEach((doc) => {
        ordersData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      ordersData.sort((a, b) => b.createdAt - a.createdAt);

      const facilityOrders = filterByFacility(ordersData, facilityId);
      setOrders(facilityOrders);
      setFilteredOrders(facilityOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const ordersRef = collection(db, 'orders');

        // Only fetch active (non-completed) orders from the server. Completed orders
        // accumulate forever and live on the Archive page, so excluding them here keeps
        // reads bounded to the active set (~40-50) regardless of how large the orders
        // collection grows. Sorting is done in-memory by the `sortedAndFilteredOrders`
        // memo, so we intentionally omit orderBy (which also avoids needing a Firestore
        // composite index). Note: a `!=` filter only returns docs that have a `status`
        // field set — every order does, so this matches the page's prior behavior.
        const querySnapshot = await getDocs(
          query(ordersRef, where('status', '!=', ORDER_STATUSES.COMPLETED))
        );

        const ordersData = filterByFacility(
          querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
          facilityId
        );

        // Only read order lines for the orders we're actually displaying, batched
        // into parallel `in` queries (Firestore allows up to 10 values per `in`).
        const orderIds = ordersData.map((o) => o.id);
        const linesMap = new Map();
        const batches = [];
        for (let i = 0; i < orderIds.length; i += 10) {
          batches.push(orderIds.slice(i, i + 10));
        }

        const lineSnaps = await Promise.all(
          batches.map((batch) =>
            getDocs(query(collection(db, 'orderLines'), where('orderId', 'in', batch)))
          )
        );

        lineSnaps.forEach((snap) => {
          snap.docs.forEach((lineDoc) => {
            const line = lineDoc.data();
            if (!linesMap.has(line.orderId)) {
              linesMap.set(line.orderId, {
                totalLines: 0,
                printedLines: 0,
                totalQuantity: 0,
                printedQuantity: 0,
                lineTypes: new Set()
              });
            }
            const counts = linesMap.get(line.orderId);
            counts.totalLines++;
            counts.totalQuantity += Number(line.totalQuantity || 0);

            const normalizedType = normalizeLineType(line.type);
            if (normalizedType) {
              counts.lineTypes.add(normalizedType);
            }

            if (line.status === 'Completed') {
              counts.printedLines++;
              counts.printedQuantity += Number(line.totalQuantity || 0);
            }
          });
        });

        const ordersWithCounts = ordersData.map((order) => {
          const lineCounts = linesMap.get(order.id) || {
            totalLines: 0,
            printedLines: 0,
            totalQuantity: 0,
            printedQuantity: 0,
            lineTypes: new Set()
          };

          return {
            ...order,
            totalLines: lineCounts.totalLines,
            printedLines: lineCounts.printedLines,
            totalQuantity: lineCounts.totalQuantity,
            printedQuantity: lineCounts.printedQuantity,
            lineTypes: sortLineTypes(lineCounts.lineTypes)
          };
        });

        if (cancelled) return;
        setOrders(ordersWithCounts);
        setFilteredOrders(ordersWithCounts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching orders:', error);
        if (!cancelled) setLoading(false);
      }
    };

    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, facilityId]);

  useEffect(() => {
    // Show success animation when arriving from new order
    if (location.state?.fromNewOrder) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
    }
  }, [location]);

  // Handle line ID barcode scan - search for a line by ID and open its order
  useEffect(() => {
    const searchForLineId = async () => {
      // Only trigger if search query looks like a Firestore document ID (20+ chars, alphanumeric)
      const trimmedQuery = searchQuery.trim();
      if (!trimmedQuery || trimmedQuery.length < 15) return;
      
      // Check if it might be a line ID (Firestore IDs are typically 20 chars)
      // Skip if it matches common search patterns (has spaces, looks like a name)
      if (trimmedQuery.includes(' ') || /^[A-Za-z]+$/.test(trimmedQuery)) return;
      
      try {
        // Try to find a line with this ID
        const lineRef = doc(db, 'orderLines', trimmedQuery);
        const { getDoc: getDocFn } = await import('firebase/firestore');
        const lineDoc = await getDocFn(lineRef);
        
        if (lineDoc.exists()) {
          const lineData = lineDoc.data();
          console.log('Found line by barcode scan:', trimmedQuery, lineData);
          
          // Find the order that contains this line
          if (lineData.orderId) {
            const orderRef = doc(db, 'orders', lineData.orderId);
            const orderDoc = await getDocFn(orderRef);
            
            if (orderDoc.exists()) {
              const orderData = { id: orderDoc.id, ...orderDoc.data() };
              console.log('Opening order from line barcode:', orderData);
              
              // Clear the search and open the order view
              setSearchQuery('');
              handleViewOrder(orderData);
            }
          }
        }
      } catch (error) {
        // Silently fail - this is just an enhancement for barcode scanning
        console.log('Line ID search failed (this is normal for non-ID searches):', error.message);
      }
    };
    
    // Debounce the search slightly to avoid too many lookups
    const timeoutId = setTimeout(searchForLineId, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleViewOrder = async (order) => {
    try {
      console.log('Fetching orderLines for order:', order.id);
      
      const linesQuery = query(
        collection(db, 'orderLines'),
        where('orderId', '==', order.id),
        orderBy('lineNumber', 'asc')
      );
      
      const linesSnapshot = await getDocs(linesQuery);
      const lines = linesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Found orderLines:', lines);

      setSelectedOrder({
        ...order,
        lines: lines
      });
    } catch (error) {
      console.error('Error fetching orderLines:', error);
      setSelectedOrder({
        ...order,
        lines: []
      });
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setIsEditing(true);
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      
      // Also delete associated order lines
      const linesRef = collection(db, 'orderLines');
      const q = query(linesRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // Update local state
      setOrders(prevOrders => 
        prevOrders.filter(order => order.id !== orderId)
      );
      
      setOrderToDelete(null); // Close confirmation modal
      alert('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order');
    }
  };

  const handleReadyToOrder = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: ORDER_STATUSES.READY_TO_ORDER,
        readyToOrder: true,
        readyToOrderDate: new Date(),
      });

      // Update all lines for this order to READY_TO_ORDER if not already further along
      const linesRef = collection(db, 'orderLines');
      const q = query(linesRef, where('orderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      const updatePromises = querySnapshot.docs.map(docSnap => {
        const line = docSnap.data();
        // Only update lines that are not already Ordered, Completed, or Printed
        if ([LINE_STATUSES.ORDERED, LINE_STATUSES.COMPLETED, LINE_STATUSES.PRINTED].includes(line.status)) {
          return Promise.resolve();
        }
        return updateDoc(docSnap.ref, {
          status: LINE_STATUSES.READY_TO_ORDER
        });
      });
      await Promise.all(updatePromises);

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? { ...order, status: ORDER_STATUSES.READY_TO_ORDER, readyToOrder: true }
            : order
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleNewOrderCreated = () => {
    setIsModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };
  const handleOpenQuoteModal = () => {
    setQuoteToEdit(null);
    setIsQuoteModalOpen(true);
  };
  const handleCloseQuoteModal = () => {
    setQuoteToEdit(null);
    setIsQuoteModalOpen(false);
  };

  // Open PDF action modal (download or email)
  const handleExportOrderPDF = (order, e) => {
    e.stopPropagation();
    setPdfActionOrder(order);
    setCustomerEmailForPdf(null);
    setPdfActionCustomerLoading(true);
  };

  // Fetch customer email when PDF action modal opens
  useEffect(() => {
    if (!pdfActionOrder?.customerId) {
      setPdfActionCustomerLoading(false);
      setCustomerEmailForPdf(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const customerSnap = await getDoc(doc(db, 'customers', pdfActionOrder.customerId));
        if (cancelled) return;
        setCustomerEmailForPdf(customerSnap.exists && customerSnap.data().email ? customerSnap.data().email : null);
      } catch (err) {
        if (!cancelled) setCustomerEmailForPdf(null);
      } finally {
        if (!cancelled) setPdfActionCustomerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfActionOrder?.customerId]);

  // Start download: render hidden Quote then run existing download effect
  const handlePdfDownload = () => {
    if (!pdfActionOrder) return;
    setGeneratingPDF(pdfActionOrder.id);
    setOrderToDownload(pdfActionOrder);
    setPdfActionOrder(null);
  };

  // Start email: render hidden Quote then run effect that generates PDF and sends email
  const handlePdfEmail = () => {
    if (!pdfActionOrder || !customerEmailForPdf) return;
    setGeneratingPDF(pdfActionOrder.id);
    setOrderToDownload({ order: pdfActionOrder, action: 'email', to: customerEmailForPdf });
    setPdfActionOrder(null);
  };

  // Effect to generate PDF once the Quote component is rendered (download or email)
  useEffect(() => {
    const order = orderToDownload?.order ?? orderToDownload;
    const action = orderToDownload?.action ?? 'download';
    const toEmail = orderToDownload?.to;
    if (!order || !orderPdfRef.current) return;

    setDownloading(true);

    const generatePdf = async () => {
      try {
        const opt = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: `Order_${order.poNumber || order.id?.slice(0, 8)}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            letterRendering: true
          },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        if (action === 'email' && toEmail) {
          setPdfEmailSending(true);
          const pdfBlob = await html2pdf().set(opt).from(orderPdfRef.current).output('blob');
          const pdfBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result?.split(',')[1];
              resolve(base64 || '');
            };
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          const sendEmail = httpsCallable(functions, 'sendEmail');
          const orderNumber = order.poNumber || order.orderNumber || order.id?.slice(0, 8);
          await sendEmail({
            to: toEmail,
            subject: `Order ${orderNumber} from 8|7`,
            text: `Your order (${orderNumber}) PDF is attached.`,
            html: `<p>Your order (${orderNumber}) PDF is attached.</p><p>Thanks,<br>8|7 Team</p>`,
            attachment: { content: pdfBase64, filename: `Order_${orderNumber}.pdf`, type: 'application/pdf' }
          });
          alert('Email sent successfully.');
        } else {
          await html2pdf().set(opt).from(orderPdfRef.current).save();
        }

        setOrderToDownload(null);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert(action === 'email' ? 'Failed to send email' : 'Failed to generate PDF');
      } finally {
        setDownloading(false);
        setGeneratingPDF(null);
        setPdfEmailSending(false);
      }
    };

    setTimeout(generatePdf, 1500);
  }, [orderToDownload]);

  const calculateOrderProgress = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) {
      return {
        totalLines: 0,
        printedLines: 0,
        totalQuantity: 0,
        printedQuantity: 0
      };
    }

    return lines.reduce((acc, line) => {
      const lineQuantity = line.totalQuantity || 0;
      
      acc.totalQuantity += lineQuantity;
      acc.totalLines += 1;
      
      // Update to use LINE_STATUSES.COMPLETED
      if (line.status === LINE_STATUSES.COMPLETED) {
        acc.printedQuantity += lineQuantity;
        acc.printedLines += 1;
      }
      
      return acc;
    }, {
      totalLines: 0,
      printedLines: 0,
      totalQuantity: 0,
      printedQuantity: 0
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <OrdersContainer>
      <HeaderContainer>
        <PageHeader>ORDERS</PageHeader>
      </HeaderContainer>
      
      <FiltersContainer>
        <SearchWrapper>
          <SearchInput
            placeholder="SEARCH ORDERS..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchWrapper>
        
        <FilterSection>
          <FilterLabel><FaFilter /> Status:</FilterLabel>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value={ORDER_STATUSES.CREATED}>Created</option>
            <option value={ORDER_STATUSES.READY_TO_ORDER}>Ready to Order</option>
            <option value={ORDER_STATUSES.ORDERED}>Ordered</option>
            <option value={ORDER_STATUSES.PARTIALLY_RECEIVED}>Partially Received</option>
            <option value={ORDER_STATUSES.RECEIVED}>Received</option>
            <option value={ORDER_STATUSES.PARTIALLY_READY_TO_PRINT}>Partially Ready to Print</option>
            <option value={ORDER_STATUSES.READY_TO_PRINT}>Ready to Print</option>
            <option value={ORDER_STATUSES.READY_TO_INVOICE}>Ready to Invoice</option>
          </FilterSelect>

        </FilterSection>
        
        <ButtonContainer>
          <CreateButton className="hide-mobile" onClick={() => setIsReorderModalOpen(true)}>+ Reorder</CreateButton>
          <CreateButton onClick={handleOpenModal}>+ Order</CreateButton>
          <CreateButton onClick={handleOpenQuoteModal}>+ Quote</CreateButton>
        </ButtonContainer>
      </FiltersContainer>

      <OrdersTable>
        <thead>
          <tr>
            <SortableTh 
              active={sortConfig.key === 'customerName'}
              onClick={() => handleSort('customerName')}
            >
              Customer {getSortIcon('customerName')}
            </SortableTh>
            <SortableTh 
              active={sortConfig.key === 'projectManager'}
              onClick={() => handleSort('projectManager')}
            >
              Project Manager {getSortIcon('projectManager')}
            </SortableTh>
            <SortableTh 
              active={sortConfig.key === 'dueDate'}
              onClick={() => handleSort('dueDate')}
            >
              Due Date {getSortIcon('dueDate')}
            </SortableTh>
            <SortableTh 
              active={sortConfig.key === 'status'}
              onClick={() => handleSort('status')}
            >
              Status {getSortIcon('status')}
            </SortableTh>
            <SortableTh 
              active={sortConfig.key === 'totalLines'}
              onClick={() => handleSort('totalLines')}
            >
              Lines {getSortIcon('totalLines')}
            </SortableTh>
            <SortableTh 
              active={sortConfig.key === 'totalQuantity'}
              onClick={() => handleSort('totalQuantity')}
            >
              Pieces {getSortIcon('totalQuantity')}
            </SortableTh>
            <SortableTh
              active={sortConfig.key === 'lineType'}
              onClick={() => handleSort('lineType')}
            >
              Type {getSortIcon('lineType')}
            </SortableTh>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredOrders.map(order => {
            const progress = calculateOrderProgress(order.lines);
            
            return (
              <TableRow 
                key={order.id}
                onClick={() => handleViewOrder(order)}
              >
                <Td>{order.customerName}</Td>
                <Td>{order.projectManager}</Td>
                <Td>{formatDate(order.dueDate)}</Td>
                <Td>
                  <StatusCell>
                    <StatusText status={order.status}>
                      {getOrderStatusDisplay(order)}
                    </StatusText>
                    {order.status === ORDER_STATUSES.CREATED && (
                      <ReadyToOrderButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadyToOrder(order.id);
                        }}
                      >
                        Push to Order
                      </ReadyToOrderButton>
                    )}
                  </StatusCell>
                </Td>
                <ProgressCell>
                  <span className="printed">{order.printedLines}</span>
                  <span className="total">/{order.totalLines}</span>
                </ProgressCell>
                <ProgressCell>
                  <span className="printed">{order.printedQuantity}</span>
                  <span className="total">/{order.totalQuantity}</span>
                </ProgressCell>
                <LineTypeCell>
                  {order.lineTypes && order.lineTypes.length > 0 ? (
                    <LineTypeBadgeRow>
                      {order.lineTypes.map(type => (
                        <LineTypeBadge
                          key={type}
                          color={getLineTypeColor(type)}
                          title={type}
                          aria-label={type}
                        >
                          {getLineTypeShortLabel(type)}
                        </LineTypeBadge>
                      ))}
                    </LineTypeBadgeRow>
                  ) : (
                    <LineTypeEmpty>—</LineTypeEmpty>
                  )}
                </LineTypeCell>
                <ActionsCell>
                  <ActionButton
                    className="hide-mobile"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOrder(order);
                    }}
                    title="View Order"
                  >
                    <FaEye />
                  </ActionButton>
                  <ActionButton
                    className="hide-mobile"
                    onClick={(e) => handleExportOrderPDF(order, e)}
                    disabled={generatingPDF === order.id}
                    title="Download Quote PDF"
                    style={{ opacity: generatingPDF === order.id ? 0.5 : 1 }}
                  >
                    <FaFileAlt />
                  </ActionButton>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOrder(order);
                    }}
                    title="Edit Order"
                  >
                    <FaPencilAlt />
                  </ActionButton>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      setOrderToDelete(order);
                    }}
                    title="Delete Order"
                  >
                    <FaTrash />
                  </ActionButton>
                </ActionsCell>
              </TableRow>
            );
          })}
        </tbody>
      </OrdersTable>

      {selectedOrder && !isEditing && (
        <OrderView 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {orderToDelete && (
        <ConfirmDeleteModal onClick={() => setOrderToDelete(null)}>
          <ConfirmDeleteContent onClick={e => e.stopPropagation()}>
            <h3>Delete Order</h3>
            <p>Are you sure you want to delete this order? This action cannot be undone.</p>
            <div className="buttons">
              <CancelButton onClick={() => setOrderToDelete(null)}>
                Cancel
              </CancelButton>
              <DeleteButton onClick={() => handleDeleteOrder(orderToDelete.id)}>
                Delete
              </DeleteButton>
            </div>
          </ConfirmDeleteContent>
        </ConfirmDeleteModal>
      )}

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} maxWidth="1200px">
        <NewOrder 
          onClose={handleCloseModal}
          onOrderCreated={handleNewOrderCreated}
          initialCustomerName={selectedOrder?.customerName}
          initialCustomerId={selectedOrder?.customerId}
          reorderDesigns={selectedOrder?.reorderDesigns}
        />
      </Modal>

      <Modal isOpen={isQuoteModalOpen} onClose={handleCloseQuoteModal} maxWidth="1200px">
        <NewQuote
          onClose={handleCloseQuoteModal}
          initialQuote={quoteToEdit}
          onSuccess={handleCloseQuoteModal}
        />
      </Modal>

      {isEditing && (
        <EditOrder 
          order={selectedOrder}
          isOpen={isEditing}
          onClose={() => {
            setIsEditing(false);
            setSelectedOrder(null);
            // Trigger a refresh of the orders list
            setRefreshTrigger(prev => prev + 1);
          }} 
        />
      )}

      {showSuccess && <SuccessAnimation onComplete={() => setShowSuccess(false)} />}

      <ReorderModal
        isOpen={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        onCreateReorder={(reorderData) => {
          setIsReorderModalOpen(false);
          setIsModalOpen(true);
          // Pass both the designs and customer data to NewOrder
          setSelectedOrder({
            reorderDesigns: reorderData.designs,
            customerName: reorderData.customer.customerName,
            customerId: reorderData.customer.customerId
          });
        }}
      />

      {/* PDF action modal: Download or Email */}
      {pdfActionOrder && (
        <Modal isOpen={!!pdfActionOrder} onClose={() => setPdfActionOrder(null)}>
          <ConfirmDeleteContent onClick={e => e.stopPropagation()}>
            <h3>Order PDF</h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>
              {pdfActionOrder.poNumber || pdfActionOrder.orderNumber || pdfActionOrder.id?.slice(0, 8)}
            </p>
            {pdfActionCustomerLoading ? (
              <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 16 }}>Loading customer...</p>
            ) : customerEmailForPdf ? (
              <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
                Email to: <strong>{customerEmailForPdf}</strong>
              </p>
            ) : (
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 16 }}>
                No customer email on file. Add an email in the customer record to send the PDF.
              </p>
            )}
            <div className="buttons">
              <CancelButton onClick={() => setPdfActionOrder(null)}>Cancel</CancelButton>
              <CreateButton onClick={handlePdfDownload}>Download PDF</CreateButton>
              <CreateButton
                onClick={handlePdfEmail}
                disabled={!customerEmailForPdf || pdfEmailSending}
                style={{ opacity: (!customerEmailForPdf || pdfEmailSending) ? 0.6 : 1 }}
              >
                {pdfEmailSending ? 'Sending…' : 'Email to customer'}
              </CreateButton>
            </div>
          </ConfirmDeleteContent>
        </Modal>
      )}

      {/* Hidden Quote component for PDF generation */}
      {orderToDownload && (
        <div style={{ position: 'fixed', left: '100vw', top: 0, width: '8.5in', background: 'white', zIndex: -1 }}>
          <div ref={orderPdfRef}>
            <Quote order={orderToDownload?.order ?? orderToDownload} type="order" hideActions={true} />
          </div>
        </div>
      )}

      {/* Loading overlay while downloading */}
      {downloading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'var(--al-surface)',
            padding: '24px 40px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ color: theme.colors.primary, fontSize: '1.1em', fontWeight: 600, marginBottom: '8px' }}>
              Generating PDF...
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9em' }}>
              Please wait while your order is being prepared for download.
            </div>
          </div>
        </div>
      )}
    </OrdersContainer>
  );
};

export default Orders; 